import {
    ALL_COUNTRIES,
    CONSENT_TITLE,
    EEP_LABELS,
    PREVIEW_CHECKS,
    MEDALLION_PREVIEW_SCREENS,
    SCREEN_LABELS,
    STEP_LABELS,
    TWO_FA_STEPS,
    VERIFICATION_MODES,
    VERIFICATION_STEP,
} from './constants';
import {
    getAcceptedDocuments,
    getAttemptText,
    getConsentTitle,
    getConsentVerifiedLine,
    getGovtIdTitle,
    getReturnLabel,
    getVerificationStepLabel,
} from './utilities';

// Ports hosted-ui's flow rules (lib/navigation.js, useVerificationStep, formatContent). They don't
// sync, so re-check those when hosted-ui changes.

const METHOD_ORDER = [PREVIEW_CHECKS.GOVT_ID, PREVIEW_CHECKS.KBA, PREVIEW_CHECKS.BANK];

const EEP_ORDER = [
    PREVIEW_CHECKS.EDUCATION,
    PREVIEW_CHECKS.EMPLOYMENT,
    PREVIEW_CHECKS.PROFESSIONAL_LICENSE,
];

const findCheck = (checks, type) =>
    (checks ?? []).find((check) => check?.verification_type === type);

// Stored as 'all', an array, or that array JSON-encoded depending on where it was read from.
const parseCountries = (value) => {
    if (value === ALL_COUNTRIES || Array.isArray(value)) {
        return value;
    }

    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const getMode = (methods) => {
    if (methods.length === 1) {
        return VERIFICATION_MODES.SINGLE;
    }

    return methods.every((method) => method.mandatory)
        ? VERIFICATION_MODES.AND
        : VERIFICATION_MODES.OR;
};

export const derivePreviewModel = (workflow) => {
    const methods = METHOD_ORDER.map((type) => findCheck(workflow?.id_verifications, type)).filter(
        (check) => check?.enabled
    );

    return {
        twoFa: Boolean(findCheck(workflow?.pre_identity_checks, PREVIEW_CHECKS.TWO_FA)?.enabled),
        risk: Boolean(findCheck(workflow?.pre_identity_checks, PREVIEW_CHECKS.RISK_SCORE)?.enabled),
        methods,
        mode: getMode(methods),
        eep: EEP_ORDER.filter((type) =>
            (workflow?.additional_checks ?? []).some(
                (check) => check?.check_type === type && check.enabled
            )
        ),
        docTypes:
            findCheck(workflow?.id_verifications, PREVIEW_CHECKS.GOVT_ID)?.metadata
                ?.allowedDocTypes ?? [],
        countries: parseCountries(workflow?.workflow?.enabled_countries),
    };
};

// Counts flow steps, not methods: consent and thank-you are excluded, risk still counts.
const getStepLabel = ({ twoFa, risk, eep }) => {
    const current = 1 + Number(twoFa) + Number(risk);
    return getVerificationStepLabel(current, current + eep.length);
};

const getSteps = ({ twoFa, eep }) =>
    twoFa
        ? [STEP_LABELS.TWO_FA, STEP_LABELS.IDENTITY, ...eep.map((type) => EEP_LABELS[type])]
        : null;

const withLabels = (screens) =>
    screens.map((entry) => ({ ...entry, label: SCREEN_LABELS[entry.screen] }));

const getThankYouScreen = (steps, returnLabel) => ({
    screen: MEDALLION_PREVIEW_SCREENS.THANK_YOU_SCREEN,
    content: { steps, returnLabel },
});

export const buildPreviewFlow = (workflow, { includeThankYou = false, companyName } = {}) => {
    const model = derivePreviewModel(workflow);
    const steps = getSteps(model);
    const eepLabels = model.eep.map((type) => EEP_LABELS[type]);

    const screens = [
        {
            screen: MEDALLION_PREVIEW_SCREENS.CONSENT_SCREEN,
            content: {
                title: getConsentTitle(eepLabels.length),
                verifiedLine: getConsentVerifiedLine(eepLabels),
            },
        },
    ];

    if (model.twoFa) {
        screens.push({ screen: MEDALLION_PREVIEW_SCREENS.TWO_FACTOR_SCREEN, content: { steps } });
    }

    if (model.methods.length) {
        screens.push({
            screen: MEDALLION_PREVIEW_SCREENS.VERIFICATION_SCREEN,
            content: {
                cards: model.methods.map(({ verification_type, attempts }) => ({
                    type: verification_type,
                    attemptText: getAttemptText(attempts),
                })),
                mode: model.mode,
                stepLabel: getStepLabel(model),
                steps,
            },
        });
    }

    const hasMethod = (type) =>
        model.methods.some(({ verification_type }) => verification_type === type);

    if (hasMethod(PREVIEW_CHECKS.GOVT_ID)) {
        screens.push({
            screen: MEDALLION_PREVIEW_SCREENS.GOVT_ID_CAPTURE_SCREEN,
            content: {
                title: getGovtIdTitle(model.docTypes),
                acceptedDocuments: getAcceptedDocuments(model.docTypes, model.countries),
                steps,
            },
        });
    }

    if (hasMethod(PREVIEW_CHECKS.KBA)) {
        screens.push({ screen: MEDALLION_PREVIEW_SCREENS.KBA_QUIZ_SCREEN, content: { steps } });
    }

    if (hasMethod(PREVIEW_CHECKS.BANK)) {
        screens.push({ screen: MEDALLION_PREVIEW_SCREENS.BANK_CONNECT_SCREEN, content: { steps } });
    }

    if (includeThankYou) {
        const hasRedirect = Boolean(workflow?.verify_ui_config?.default_redirect_url);
        screens.push(getThankYouScreen(steps, hasRedirect ? getReturnLabel(companyName) : null));
    }

    return withLabels(screens);
};

export const SAMPLE_PREVIEW_FLOW = withLabels([
    {
        screen: MEDALLION_PREVIEW_SCREENS.CONSENT_SCREEN,
        content: { title: CONSENT_TITLE, verifiedLine: getConsentVerifiedLine([]) },
    },
    { screen: MEDALLION_PREVIEW_SCREENS.TWO_FACTOR_SCREEN, content: { steps: TWO_FA_STEPS } },
    {
        screen: MEDALLION_PREVIEW_SCREENS.VERIFICATION_SCREEN,
        content: {
            cards: METHOD_ORDER.map((type) => ({ type, attemptText: getAttemptText(2) })),
            mode: VERIFICATION_MODES.AND,
            stepLabel: VERIFICATION_STEP,
            steps: TWO_FA_STEPS,
        },
    },
    { screen: MEDALLION_PREVIEW_SCREENS.KBA_QUIZ_SCREEN, content: {} },
]);

export const getSampleFlowWithThankYou = (companyName) => [
    ...SAMPLE_PREVIEW_FLOW,
    ...withLabels([getThankYouScreen(TWO_FA_STEPS, getReturnLabel(companyName))]),
];
